// Components==============
import { motion } from 'framer-motion';
import React, { useContext, useEffect } from 'react';
import { GlobalContext } from 'utilities/GlobalContext';
import { CalendarContext } from '../../Calendar';
import './Days.module.scss';
// =========================

const framerVariants = {
    initial: {
        background: '#bdccd4',
    },
    open: {
        background: '#1368e5',
    },
    finished: {
        background: '#16D872',
    },
};

const Activity = ({
    activity,
    status,
    setStatus,
}: {
    activity: 'lezen' | 'spellen';
    status: Day['day'];
    setStatus: (newData: any) => void;
}) => {
    const { logbookAuthenticated } = useContext(GlobalContext);

    function handleClick() {
        // Toggle value between open and initial
        if (logbookAuthenticated) {
            return setStatus({
                [activity]: status === 'open' ? 'initial' : 'open',
            });
        }

        // ignore if default
        if (status === 'initial') return setStatus({ [activity]: 'finished' });

        // Set status to finished
        if (status === 'open') return setStatus({ [activity]: 'finished' });

        // set status back to open
        if (status === 'finished') return setStatus({ [activity]: 'initial' });
    }

    return (
        <motion.div
            styleName="item"
            onClick={handleClick}
            animate={status}
            initial="initial"
            variants={framerVariants}
            style={{
                cursor: 'pointer',
            }}
        >
            <p>
                {status === 'open'
                    ? activity
                    : status === 'finished'
                    ? activity
                    : logbookAuthenticated
                    ? activity
                    : ''}
            </p>
        </motion.div>
    );
};

const Day = ({ day }: { day: Day }) => {
    const { logbookAuthenticated } = useContext(GlobalContext);

    const { setActiveLog, activeLog } = useContext(CalendarContext);

    function setStatus(newData: any) {
        const newValue = activeLog.attributes.days.map((d) => {
            if (d.day_id === day.day_id) {
                return { ...d, ...newData };
            }
            return d;
        });

        setActiveLog((prev) => ({
            ...prev,
            attributes: {
                ...prev.attributes,
                days: newValue,
            },
        }));
    }

    useEffect(() => {
        // Set day open
        if (
            day.day === 'initial' &&
            (day.lezen !== 'initial' || day.spellen !== 'initial')
        ) {
            return setStatus({ day: 'open' });
        }

        // Close day if no items are selected
        if (
            (day.day === 'open' || day.day === 'finished') &&
            day.lezen === 'initial' &&
            day.spellen === 'initial'
        )
            return setStatus({ day: 'initial' });

        // Set day to finished if all items are completed
        if (
            !logbookAuthenticated &&
            day.day === 'open' &&
            ((day.lezen === 'finished' && day.spellen === 'initial') ||
                (day.lezen === 'initial' && day.spellen === 'finished') ||
                (day.lezen === 'finished' && day.spellen === 'finished'))
        )
            return setStatus({ day: 'finished' });

        // Set day from finished to open if item is unfinished
        if (
            day.day === 'finished' &&
            (day.lezen === 'open' || day.spellen === 'open')
        )
            return setStatus({ day: 'open' });
    }, [day]);

    return (
        <div styleName="day-wrapper">
            <motion.div
                styleName="day"
                animate={day.day}
                initial="initial"
                variants={framerVariants}
            >
                {day.day_id === 0
                    ? 'Maandag'
                    : day.day_id === 1
                    ? 'Dinsdag'
                    : day.day_id === 2
                    ? 'Woensdag'
                    : day.day_id === 3
                    ? 'Donderdag'
                    : day.day_id === 4
                    ? 'Vrijdag'
                    : day.day_id === 5
                    ? 'Zaterdag'
                    : 'Zondag'}
            </motion.div>
            <Activity
                activity="lezen"
                status={day.lezen}
                setStatus={setStatus}
            />
            <Activity
                activity="spellen"
                status={day.spellen}
                setStatus={setStatus}
            />
        </div>
    );
};

export default function Days() {
    const { activeLog } = useContext(CalendarContext);

    const calcDay = (day: number) => {
        if (day === 0) return 6;
        return day - 1;
    };

    const parseDate = calcDay(
        new Date(activeLog.attributes.firstDayOfWeek).getDay()
    );

    const days = activeLog?.attributes?.days;
    const startOfWeek = days?.slice(parseDate, 7);
    const endOfWeek = days?.slice(0, parseDate);
    const week = [...startOfWeek, ...endOfWeek];

    return (
        <div styleName="wrapper">
            {week.map((day) => (
                <Day key={day?.day_id} day={day} />
            ))}
        </div>
    );
}
